import { MATERIALS, MaterialProperties, SupportType, SUPPORT_COEFFICIENTS, SAFETY_FACTORS } from './MaterialDatabase';

// Interfejsy dla obliczeń
export interface PlateCalculationInput {
  material: string;
  thickness: number; // mm
  width: number; // mm
  length: number; // mm
  supportType: SupportType;
  loadType: 'uniform' | 'point';
  load: number; // kg (uniform: kg/m², point: kg)
  loadPosition?: { x: number; y: number }; // dla obciążenia punktowego
  safetyFactor?: number;
}

export interface CalculationResult {
  maxDeflection: number; // mm
  maxStress: number; // MPa
  safetyFactor: number;
  percentOfMaxStress: number; // %
  isSafe: boolean;
  recommendations: string[];
  alternativeSolutions: AlternativeSolution[];
  visualizationData?: any; // dane do wizualizacji 3D
}

export interface AlternativeSolution {
  description: string;
  material?: string;
  thickness?: number;
  additionalSupport?: string;
  estimatedCost?: number;
  weightReduction?: number; // %
}

export interface BeamCalculationInput {
  material: string;
  crossSection: {
    type: 'rectangular' | 'circular' | 'tube' | 'profile';
    dimensions: any; // zależne od typu
  };
  length: number; // mm
  supportType: 'simply_supported' | 'cantilever' | 'fixed_both';
  loads: Array<{
    type: 'point' | 'uniform';
    value: number; // kg lub kg/m
    position?: number; // mm od początku
  }>;
  safetyFactor?: number;
}

export class StrengthCalculator {
  
  /**
   * Oblicz wytrzymałość płyty/panelu
   */
  static calculatePlateStrength(input: PlateCalculationInput): CalculationResult {
    const material = MATERIALS[input.material];
    if (!material) {
      throw new Error(`Nieznany materiał: ${input.material}`);
    }

    const safetyFactor = input.safetyFactor || SAFETY_FACTORS.display;
    
    // Konwersje jednostek
    const thickness_m = input.thickness / 1000;
    const width_m = input.width / 1000;
    const length_m = input.length / 1000;
    
    // Moduł sprężystości w Pa
    const E = material.modulusOfElasticity * 1e6;
    
    // Współczynnik Poissona
    const nu = material.poissonsRatio || 0.3;
    
    // Sztywność płytowa (plate rigidity) - WAŻNE DLA PŁYT!
    const D = (E * Math.pow(thickness_m, 3)) / (12 * (1 - Math.pow(nu, 2)));
    
    // Moment bezwładności dla belki (używany tylko dla przypadków belkowych)
    const I = (width_m * Math.pow(thickness_m, 3)) / 12;
    
    let maxDeflection = 0;
    let maxStress = 0;
    
    if (input.loadType === 'uniform') {
      // Obciążenie równomierne w N/m²
      const q = input.load * 9.81; // kg/m² -> N/m²
      
      if (input.supportType === 'simply_supported') {
        // Belka podparta na dwóch końcach
        const q_linear = q * width_m; // N/m (obciążenie liniowe)
        maxDeflection = (5 * q_linear * Math.pow(length_m, 4)) / (384 * E * I) * 1000; // mm
        maxStress = (q_linear * Math.pow(length_m, 2)) / (8 * (width_m * Math.pow(thickness_m, 2) / 6)) / 1e6; // MPa
        
      } else if (input.supportType === 'four_sides') {
        // Płyta podparta na 4 bokach - teoria płyt Kirchhoffa
        const a = Math.min(width_m, length_m);
        const b = Math.max(width_m, length_m);
        const ratio = a / b;
        
        // Współczynniki z tablic Timoshenko dla płyt prostokątnych
        const alpha = 0.00406 + 0.00896 * ratio - 0.00316 * Math.pow(ratio, 2);
        const beta = 0.287 + 0.196 * ratio - 0.103 * Math.pow(ratio, 2);
        
        // Ugięcie maksymalne - zależy od D (czyli grubość^3!)
        maxDeflection = (alpha * q * Math.pow(a, 4)) / D * 1000;
        
        // Naprężenie maksymalne - zależy od grubość^2
        maxStress = (beta * q * Math.pow(a, 2)) / Math.pow(thickness_m, 2) / 1e6;
        
      } else if (input.supportType === 'cantilever') {
        // Wspornik
        const q_linear = q * width_m;
        maxDeflection = (q_linear * Math.pow(length_m, 4)) / (8 * E * I) * 1000;
        maxStress = (3 * q_linear * Math.pow(length_m, 2)) / (2 * width_m * thickness_m) / 1e6;
        
      } else if (input.supportType === 'three_sides') {
        // Płyta podparta na 3 bokach
        const coefficient = SUPPORT_COEFFICIENTS[input.supportType] || 0.013;
        maxDeflection = (coefficient * q * Math.pow(length_m, 4)) / D * 1000;
        maxStress = (0.5 * q * Math.pow(length_m, 2)) / Math.pow(thickness_m, 2) / 1e6;
      }
      
    } else if (input.loadType === 'point') {
      // Obciążenie punktowe w N
      const P = input.load * 9.81; // kg -> N
      
      if (input.supportType === 'simply_supported') {
        // Belka z obciążeniem punktowym w środku
        maxDeflection = (P * Math.pow(length_m, 3)) / (48 * E * I) * 1000;
        maxStress = (P * length_m) / (4 * (width_m * Math.pow(thickness_m, 2) / 6)) / 1e6;
        
      } else if (input.supportType === 'four_sides') {
        // Płyta z obciążeniem punktowym w środku - używamy już obliczonego D
        const a = Math.min(width_m, length_m);
        
        maxDeflection = (P * Math.pow(a, 2)) / (16 * Math.PI * D) * 1000;
        maxStress = (0.6 * P) / Math.pow(thickness_m, 2) / 1e6;
        
      } else if (input.supportType === 'cantilever') {
        // Wspornik z obciążeniem na końcu
        maxDeflection = (P * Math.pow(length_m, 3)) / (3 * E * I) * 1000;
        maxStress = (P * length_m) / ((width_m * Math.pow(thickness_m, 2)) / 6) / 1e6;
      }
    }
    
    // Oblicz procent wykorzystania wytrzymałości
    const percentOfMaxStress = (maxStress / material.flexuralStrength) * 100;
    const isSafe = percentOfMaxStress < (100 / safetyFactor);
    
    // Generuj rekomendacje
    const recommendations = this.generateRecommendations(
      input, 
      maxDeflection, 
      maxStress, 
      material, 
      percentOfMaxStress
    );
    
    // Znajdź alternatywne rozwiązania
    const alternativeSolutions = this.findAlternativeSolutions(
      input,
      maxDeflection,
      maxStress
    );
    
    return {
      maxDeflection: Math.round(maxDeflection * 100) / 100,
      maxStress: Math.round(maxStress * 10) / 10,
      safetyFactor: safetyFactor,
      percentOfMaxStress: Math.round(percentOfMaxStress),
      isSafe,
      recommendations,
      alternativeSolutions,
      visualizationData: this.generateVisualizationData(input, maxDeflection, maxStress)
    };
  }
  
  /**
   * Oblicz wytrzymałość belki/profilu
   */
  static calculateBeamStrength(input: BeamCalculationInput): CalculationResult {
    const material = MATERIALS[input.material];
    if (!material) {
      throw new Error(`Nieznany materiał: ${input.material}`);
    }
    
    // Oblicz moment bezwładności na podstawie przekroju
    const I = this.calculateMomentOfInertia(input.crossSection);
    const A = this.calculateCrossSectionArea(input.crossSection);
    const c = this.calculateMaxDistance(input.crossSection); // odległość od osi neutralnej
    
    const E = material.modulusOfElasticity * 1e6; // Pa
    const length_m = input.length / 1000;
    
    // Oblicz maksymalny moment gnący i siłę tnącą
    const { maxMoment, maxShear, maxDeflection } = this.calculateBeamForces(
      input.loads,
      input.length,
      input.supportType,
      E,
      I
    );
    
    // Naprężenia gnące
    const bendingStress = (maxMoment * c) / I / 1e6; // MPa
    
    // Naprężenia ścinające
    const shearStress = maxShear / A / 1e6; // MPa
    
    // Naprężenia zastępcze (von Mises)
    const vonMisesStress = Math.sqrt(
      Math.pow(bendingStress, 2) + 3 * Math.pow(shearStress, 2)
    );
    
    const safetyFactor = input.safetyFactor || SAFETY_FACTORS.structural;
    const percentOfMaxStress = (vonMisesStress / material.flexuralStrength) * 100;
    const isSafe = percentOfMaxStress < (100 / safetyFactor);
    
    return {
      maxDeflection: Math.round(maxDeflection * 100) / 100,
      maxStress: Math.round(vonMisesStress * 10) / 10,
      safetyFactor,
      percentOfMaxStress: Math.round(percentOfMaxStress),
      isSafe,
      recommendations: [],
      alternativeSolutions: []
    };
  }
  
  /**
   * Oblicz optymalną grubość materiału
   */
  static calculateOptimalThickness(
    material: string,
    dimensions: { width: number; length: number },
    maxLoad: number,
    maxAllowedDeflection: number,
    supportType: SupportType
  ): number {
    const mat = MATERIALS[material];
    if (!mat) {
      throw new Error(`Nieznany materiał: ${material}`);
    }
    
    // Iteracyjnie szukaj optymalnej grubości
    const thicknesses = [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30];
    
    for (const thickness of thicknesses) {
      const result = this.calculatePlateStrength({
        material,
        thickness,
        width: dimensions.width,
        length: dimensions.length,
        supportType,
        loadType: 'uniform',
        load: maxLoad
      });
      
      if (result.isSafe && result.maxDeflection <= maxAllowedDeflection) {
        return thickness;
      }
    }
    
    return -1; // Nie znaleziono odpowiedniej grubości
  }
  
  /**
   * Generuj rekomendacje na podstawie wyników
   */
  private static generateRecommendations(
    input: PlateCalculationInput,
    deflection: number,
    stress: number,
    material: MaterialProperties,
    percentStress: number
  ): string[] {
    const recommendations: string[] = [];
    
    // Sprawdź ugięcie
    const maxAllowedDeflection = Math.min(input.length, input.width) / 250; // L/250
    if (deflection > maxAllowedDeflection) {
      recommendations.push(
        `⚠️ Ugięcie ${deflection.toFixed(1)}mm przekracza dopuszczalne ${maxAllowedDeflection.toFixed(1)}mm (L/250)`
      );
      recommendations.push(
        `💡 Zwiększ grubość materiału lub dodaj dodatkowe podpory`
      );
    }
    
    // Sprawdź naprężenia
    if (percentStress > 80) {
      recommendations.push(
        `⚠️ Materiał pracuje na ${percentStress}% swojej wytrzymałości`
      );
      if (percentStress > 100) {
        recommendations.push(
          `❌ PRZEKROCZONA WYTRZYMAŁOŚĆ! Konstrukcja niebezpieczna!`
        );
      }
    } else if (percentStress < 30) {
      recommendations.push(
        `✅ Materiał wykorzystany tylko w ${percentStress}% - możliwa optymalizacja`
      );
    }
    
    // Sprawdź temperaturę
    if (!material.uvResistance && input.supportType === 'four_sides') {
      recommendations.push(
        `💡 Rozważ materiał z ochroną UV dla zastosowań zewnętrznych`
      );
    }
    
    // Dodatkowe wzmocnienia
    if (deflection > maxAllowedDeflection * 0.7) {
      recommendations.push(
        `💡 Rozważ dodanie żeber usztywniających lub ramy`
      );
    }
    
    return recommendations;
  }
  
  /**
   * Znajdź alternatywne rozwiązania
   */
  private static findAlternativeSolutions(
    input: PlateCalculationInput,
    currentDeflection: number,
    currentStress: number
  ): AlternativeSolution[] {
    const solutions: AlternativeSolution[] = [];
    const currentMaterial = MATERIALS[input.material];
    
    // 1. Grubszy materiał
    if (currentStress > currentMaterial.flexuralStrength * 0.5) {
      const newThickness = input.thickness * 1.5;
      solutions.push({
        description: `Zwiększ grubość do ${newThickness}mm`,
        thickness: newThickness,
        estimatedCost: 150, // % kosztu
        weightReduction: -50 // zwiększenie wagi o 50%
      });
    }
    
    // 2. Mocniejszy materiał
    Object.entries(MATERIALS).forEach(([key, mat]) => {
      if (mat.flexuralStrength > currentMaterial.flexuralStrength * 1.5) {
        solutions.push({
          description: `Użyj ${mat.displayName} zamiast ${currentMaterial.displayName}`,
          material: key,
          estimatedCost: Math.round((mat.pricePerKg / currentMaterial.pricePerKg) * 100),
          weightReduction: Math.round(((mat.density - currentMaterial.density) / currentMaterial.density) * 100)
        });
      }
    });
    
    // 3. Dodatkowe podpory
    if (input.supportType === 'simply_supported') {
      solutions.push({
        description: 'Dodaj podporę w środku rozpiętości',
        additionalSupport: 'central_support',
        estimatedCost: 120,
        weightReduction: 0
      });
    }
    
    // 4. Rama wzmacniająca
    solutions.push({
      description: 'Dodaj aluminiową ramę po obwodzie',
      additionalSupport: 'aluminum_frame',
      estimatedCost: 180,
      weightReduction: 10
    });
    
    return solutions.slice(0, 4); // Zwróć max 4 najlepsze rozwiązania
  }
  
  /**
   * Generuj dane do wizualizacji 3D
   */
  private static generateVisualizationData(
    input: PlateCalculationInput,
    deflection: number,
    stress: number
  ): any {
    // Siatka punktów do wizualizacji ugięcia
    const gridSize = 20;
    const points: number[][] = [];
    
    for (let i = 0; i <= gridSize; i++) {
      const row: number[] = [];
      for (let j = 0; j <= gridSize; j++) {
        const x = (i / gridSize) * input.width;
        const y = (j / gridSize) * input.length;
        
        // Przybliżone ugięcie w danym punkcie (uproszczone)
        let z = 0;
        if (input.supportType === 'simply_supported') {
          // Paraboliczny kształt ugięcia
          z = deflection * Math.sin((Math.PI * i) / gridSize) * Math.sin((Math.PI * j) / gridSize);
        } else if (input.supportType === 'four_sides') {
          // Ugięcie maksymalne w środku
          const distFromCenterX = Math.abs(i - gridSize/2) / (gridSize/2);
          const distFromCenterY = Math.abs(j - gridSize/2) / (gridSize/2);
          z = deflection * (1 - distFromCenterX) * (1 - distFromCenterY);
        }
        
        row.push(z);
      }
      points.push(row);
    }
    
    return {
      deflectionGrid: points,
      maxDeflectionPoint: { x: input.width / 2, y: input.length / 2, z: deflection },
      stressDistribution: this.calculateStressDistribution(input, stress),
      colorMap: this.generateColorMap(stress, MATERIALS[input.material].flexuralStrength)
    };
  }
  
  /**
   * Oblicz rozkład naprężeń
   */
  private static calculateStressDistribution(
    input: PlateCalculationInput,
    maxStress: number
  ): number[][] {
    const grid: number[][] = [];
    const gridSize = 10;
    
    for (let i = 0; i <= gridSize; i++) {
      const row: number[] = [];
      for (let j = 0; j <= gridSize; j++) {
        // Uproszczony rozkład naprężeń
        let stress = maxStress;
        
        if (input.supportType === 'simply_supported') {
          // Maksymalne naprężenia w środku
          const distFromCenter = Math.sqrt(
            Math.pow(i - gridSize/2, 2) + Math.pow(j - gridSize/2, 2)
          ) / (gridSize/2);
          stress = maxStress * (1 - distFromCenter * 0.5);
        }
        
        row.push(stress);
      }
      grid.push(row);
    }
    
    return grid;
  }
  
  /**
   * Generuj mapę kolorów dla wizualizacji
   */
  private static generateColorMap(stress: number, maxAllowedStress: number): any {
    const ratio = stress / maxAllowedStress;
    
    return {
      safe: ratio < 0.5 ? '#00ff00' : ratio < 0.8 ? '#ffff00' : '#ff0000',
      gradient: [
        { value: 0, color: '#0000ff' },
        { value: 0.25, color: '#00ff00' },
        { value: 0.5, color: '#ffff00' },
        { value: 0.75, color: '#ff8800' },
        { value: 1, color: '#ff0000' }
      ]
    };
  }
  
  // Pomocnicze metody dla belek
  private static calculateMomentOfInertia(crossSection: any): number {
    switch (crossSection.type) {
      case 'rectangular':
        const b = crossSection.dimensions.width / 1000; // m
        const h = crossSection.dimensions.height / 1000; // m
        return (b * Math.pow(h, 3)) / 12;
        
      case 'circular':
        const d = crossSection.dimensions.diameter / 1000; // m
        return (Math.PI * Math.pow(d, 4)) / 64;
        
      case 'tube':
        const do_outer = crossSection.dimensions.outerDiameter / 1000;
        const di_inner = crossSection.dimensions.innerDiameter / 1000;
        return (Math.PI * (Math.pow(do_outer, 4) - Math.pow(di_inner, 4))) / 64;
        
      default:
        return 0;
    }
  }
  
  private static calculateCrossSectionArea(crossSection: any): number {
    switch (crossSection.type) {
      case 'rectangular':
        return (crossSection.dimensions.width / 1000) * (crossSection.dimensions.height / 1000);
        
      case 'circular':
        const r = crossSection.dimensions.diameter / 2000;
        return Math.PI * Math.pow(r, 2);
        
      case 'tube':
        const ro = crossSection.dimensions.outerDiameter / 2000;
        const ri = crossSection.dimensions.innerDiameter / 2000;
        return Math.PI * (Math.pow(ro, 2) - Math.pow(ri, 2));
        
      default:
        return 0;
    }
  }
  
  private static calculateMaxDistance(crossSection: any): number {
    switch (crossSection.type) {
      case 'rectangular':
        return crossSection.dimensions.height / 2000; // m
        
      case 'circular':
      case 'tube':
        return crossSection.dimensions.outerDiameter / 2000; // m
        
      default:
        return 0;
    }
  }
  
  private static calculateBeamForces(
    loads: any[],
    length: number,
    supportType: string,
    E: number,
    I: number
  ): { maxMoment: number; maxShear: number; maxDeflection: number } {
    // Uproszczone obliczenia dla pojedynczego obciążenia
    // W pełnej wersji należy użyć metody elementów skończonych lub superpozycji
    
    const length_m = length / 1000;
    let maxMoment = 0;
    let maxShear = 0;
    let maxDeflection = 0;
    
    loads.forEach(load => {
      if (load.type === 'point') {
        const P = load.value * 9.81; // N
        const a = (load.position || length/2) / 1000; // m
        const b = length_m - a;
        
        if (supportType === 'simply_supported') {
          // Reakcje podporowe
          const R1 = (P * b) / length_m;
          const R2 = (P * a) / length_m;
          
          // Maksymalny moment w punkcie przyłożenia siły
          maxMoment = Math.max(maxMoment, (R1 * a));
          maxShear = Math.max(maxShear, Math.max(R1, R2));
          
          // Ugięcie w punkcie przyłożenia siły
          const deflection = (P * a * b * (length_m * length_m - a * a - b * b)) / (6 * E * I * length_m);
          maxDeflection = Math.max(maxDeflection, deflection * 1000);
        }
      }
    });
    
    return { maxMoment, maxShear, maxDeflection };
  }
}

// Eksportuj singleton dla łatwego użycia
export const strengthCalculator = new StrengthCalculator();
